/**
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React, { useRef, useMemo, useContext, useLayoutEffect } from 'react';
import { useDispatch } from 'react-redux';
import styled, { withTheme } from 'styled-components';
import GoogleMapsControl, { MapControlOrderIndex } from '../map/GoogleMapsControl';
import StyledProps from '../theme/AppThemeInterface';
import StraightenIcon from '@mui/icons-material/Straighten';
import { MapActionsContext, useSelectorWithMapId } from '../map/BaseMapContainer';
import { selectMapRulerToolShown } from '../../../store';
import { Tooltip } from '@mui/material';

interface RulerToolMapControlProps extends StyledProps {
  map: google.maps.Map;
  isFullScreen: boolean;
}

export function RulerToolMapControl({ theme, map, isFullScreen }: RulerToolMapControlProps) {
  const dispatch = useDispatch();
  const mapActionsContext = useContext(MapActionsContext);
  const mapRulerToolShown = useSelectorWithMapId(selectMapRulerToolShown);
  const { toggleMapRulerTool } = mapActionsContext.mapActions;

  const mapRulerToolIcon = useRef<HTMLElement>();

  useLayoutEffect(() => {
    if (mapRulerToolIcon.current && mapRulerToolIcon.current.classList) {
      if (mapRulerToolShown) {
        mapRulerToolIcon.current.classList.add('map_ruler_tool_icon--active');
      } else {
        mapRulerToolIcon.current.classList.remove('map_ruler_tool_icon--active');
      }
    }
  }, [mapRulerToolShown]);

  const items = useMemo(
    () => [
      {
        render: () => (
          <Tooltip
            arrow
            title="Measure Distance"
            placement="left-start"
            componentsProps={{
              tooltip: {
                sx: { backgroundColor: theme.tooltipBackgroundColor },
              },
              arrow: {
                sx: { color: theme.tooltipBackgroundColor },
              },
            }}
          >
            <StyledLabel id="map_ruler_tool_btn" ref={mapRulerToolIcon} />
          </Tooltip>
        ),
        buttonTextColor: theme.secondaryButtonTextColor,
        id: 'map_ruler_tool_btn_container',
        onClick: () => {
          dispatch(toggleMapRulerTool());
        },
      },
    ],
    [mapRulerToolIcon, dispatch, theme, toggleMapRulerTool],
  );

  return (
    <GoogleMapsControl
      items={items}
      map={map}
      mapPosition={google.maps.ControlPosition.RIGHT_TOP}
      height={32}
      hidden={isFullScreen}
      index={MapControlOrderIndex.MAP_RULER_TOOL_CONTROL}
    />
  );
}

export default withTheme(RulerToolMapControl);

const StyledLabel = styled(StraightenIcon)`
  width: 20px !important;
`;
